class KpiCard extends HTMLElement {
  constructor() {
    super();
    this.hasAnimated = false;
    this.observer = null;
    this.animationFrame = null;
    this.animating = false;

    this.animationDuration = parseInt(this.dataset.animationDuration ?? "2000");
    this.decimalPlaces = parseInt(this.dataset.decimalPlaces ?? "0");
    this.targetValue = this.dataset.targetValue !== undefined ? parseFloat(this.dataset.targetValue) : 0;
    this.startValue = this.dataset.startValue !== undefined ? parseFloat(this.dataset.startValue) : 0;
    this.once = this.dataset.once !== "false";
    this.counter = this.querySelector(".kpi-card__counter");
  }

  connectedCallback() {
    if (!this.targetValue || !this.counter) return;

    if (this.dataset.ariaLive !== "off") {
      this.counter.setAttribute("aria-live", "polite");
      this.counter.setAttribute("aria-atomic", "true");
    }

    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.updateCounter(this.targetValue);
      this.hasAnimated = true;
      return;
    }

    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    this.teardownObserver();
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
  }

  setupIntersectionObserver() {
    if (!("IntersectionObserver" in window)) {
      this.startAnimationOnce();
      return;
    }

    const options = { threshold: 0.3, rootMargin: "0px 0px -10% 0px" };

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.startAnimationOnce();
          if (this.once) this.teardownObserver();
        }
      }
    }, options);

    this.observer.observe(this);
  }

  teardownObserver() {
    if (this.observer) {
      this.observer.unobserve(this);
      this.observer.disconnect();
      this.observer = null;
    }
  }

  startAnimationOnce() {
    if (this.hasAnimated && this.once) return;
    if (this.animating) return;

    this.hasAnimated = true;
    this.startAnimation();
  }

  startAnimation() {
    this.animating = true;

    const startTime = performance.now();
    const startValue = this.startValue;
    const endValue = this.targetValue;
    const duration = Math.max(0, this.animationDuration);

    /**
     * @param {number} currentTime
     */
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = duration === 0 ? 1 : Math.min(elapsed / duration, 1);
      const easedProgress = this.easeOutCubic(progress);
      const currentValue = startValue + (endValue - startValue) * easedProgress;

      this.updateCounter(currentValue);

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.updateCounter(endValue);
        this.animating = false;
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  /**
   * @param {number} t
   */
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * @param {number} value
   */
  updateCounter(value) {
    if (!this.counter) return;
    this.counter.textContent = this.formatNumber(value);
  }

  /**
   * @param {number} value
   */

  formatNumber(value) {
    const decimals = Math.max(0, this.decimalPlaces);

    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(decimals === 0 ? Math.floor(value) : value);
  }
}

if (!customElements.get("kpi-card")) {
  customElements.define("kpi-card", KpiCard);
}
